import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { scopes } from '@tw/lib';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private msalService = inject(MsalService);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return next.handle(req);
    }

    const silentReq = { loginHint: account.username, scopes: scopes };

    const withAuthHeader = (token: string) =>
      req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

    return from(this.msalService.acquireTokenSilent(silentReq)).pipe(
      switchMap((result) => next.handle(withAuthHeader(result.accessToken))),
      catchError((error) => {
        if (
          // Note: for now enabled for below - acquireTokenSilent error.
          // "AADSTS160021: Application requested a user session which does not exist."
          error instanceof InteractionRequiredAuthError ||
          error?.errorMessage?.includes('AADSTS160021')
        ) {
          console.warn('acquireTokenSilent failed, attempting silent SSO...');
          return from(this.msalService.ssoSilent(silentReq)).pipe(
            switchMap((res) => next.handle(withAuthHeader(res.accessToken))),
            catchError(() => {
              console.warn('Silent SSO failed, redirecting to login.');
              this.msalService.loginRedirect();
              return throwError(() => error);
            }),
          );
        }

        // Silent renewal via the hidden iframe timed out or was blocked
        // (e.g. third-party cookies, iframe navigation error). Recover with an
        // interactive redirect instead of failing the request.
        if (
          error instanceof BrowserAuthError &&
          (error.errorCode === 'monitor_window_timeout' ||
            error.errorCode === 'iframe_closed_prematurely' ||
            error.errorCode === 'silent_sso_error')
        ) {
          console.warn(
            'Silent token renewal timed out/blocked, redirecting to re-authenticate...',
          );
          this.msalService.acquireTokenRedirect({ scopes, account });
          return throwError(() => error);
        }

        return throwError(() => error);
      }),
    );
  }
}
